import * as React from 'react';

import { Box, Typography } from '@mui/material';
import { ResponsiveScatterPlotCanvas } from '@nivo/scatterplot';

import {
  SERIES_LABEL,
  type ExplorerPoint,
  type PointDatum,
  type ViewBox,
  toSeries,
} from './explorerPoints';

/**
 * The flavor space, as the model laid it out.
 *
 * These are UMAP coordinates from the training run. UMAP preserves local
 * neighbourhoods: two points close together really are similar. It does not
 * preserve global distance, and the axes have no units and no direction — so
 * the gap between two distant groups means nothing, and neither does "left" or
 * "up". The axis labels say so rather than naming a flavor, and no cluster
 * boundary is drawn, because the model does not store any.
 *
 * Canvas, not SVG: a real universe is ~16k products, and one DOM node each is
 * both slow to mount and a large share of a crashed tab's memory.
 *
 * The domain is supplied rather than inferred. nivo defaults both axes to
 * `min: 0` and UMAP coordinates are routinely negative, which drew most of the
 * universe outside the plot — and every member of a norm set that happened to
 * sit in the negative quadrant.
 */

/**
 * nivo's canvas components are typed against `{ x, y }` and are not generic, but
 * they hand each datum back untouched — so the product name rides along at
 * runtime and is read back here rather than resolved by coordinate, which would
 * be ambiguous the moment two products land on the same point.
 */
const productOf = (d: unknown): string =>
  (d as PointDatum | undefined)?.product ?? '';

const COLOR: Record<string, string> = {
  [SERIES_LABEL.anchor]: '#d1495b',
  [SERIES_LABEL.included]: '#3a7ca5',
  [SERIES_LABEL.matched]: '#e9a13b',
  // Faint: sixteen thousand of these at full strength buried the hundred that
  // the screen is actually about.
  [SERIES_LABEL.other]: 'rgba(150,155,165,0.45)',
};

/**
 * Weight follows meaning. Every point used to be the same size, so the crowd
 * carried as much ink as the norm set standing in it.
 */
const SIZE: Record<string, number> = {
  [SERIES_LABEL.anchor]: 11,
  [SERIES_LABEL.included]: 7,
  [SERIES_LABEL.matched]: 7,
  [SERIES_LABEL.other]: 3,
};

type Props = {
  points: ExplorerPoint[];
  onSelect: (product: string) => void;
  selected?: string;
  /** the window to draw; both axes carry the same span so distance is honest */
  box: ViewBox | null;
  /** points outside the window, reported rather than silently dropped */
  offscreen?: number;
};

const FlavorSpaceMap: React.FC<Props> = ({
  points,
  onSelect,
  selected,
  box,
  offscreen = 0,
}) => {
  const series = React.useMemo(() => toSeries(points), [points]);

  if (points.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No products to place on the map here.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Square, and meant literally: the inner plot has equal width and
          height, and both axes carry the same span, so a unit of x is drawn
          the same size as a unit of y. Stretching one would distort the only
          thing the picture is good for. The legend sits below rather than in a
          right margin, which is what makes the inner area square. */}
      <Box sx={{ width: '100%', maxWidth: 520, aspectRatio: '1 / 1' }}>
        <ResponsiveScatterPlotCanvas
          data={series}
          margin={{ top: 16, right: 16, bottom: 56, left: 56 }}
          xScale={
            box
              ? { type: 'linear', min: box.x.min, max: box.x.max }
              : { type: 'linear', min: 'auto', max: 'auto' }
          }
          yScale={
            box
              ? { type: 'linear', min: box.y.min, max: box.y.max }
              : { type: 'linear', min: 'auto', max: 'auto' }
          }
          nodeSize={(d) =>
            productOf(d.data) === selected ? 15 : SIZE[String(d.serieId)] ?? 3
          }
          colors={(s: { serieId: string | number }) =>
            COLOR[String(s.serieId)] ?? 'rgba(150,155,165,0.45)'
          }
          onClick={(node) => onSelect(productOf(node.data))}
          tooltip={({ node }) => (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                backgroundColor: 'background.paper',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">{productOf(node.data)}</Typography>
            </Box>
          )}
          axisBottom={{ legend: 'UMAP dimension 1', legendOffset: 40 }}
          axisLeft={{ legend: 'UMAP dimension 2', legendOffset: -44 }}
        />
      </Box>

      {/* Below the plot, so the inner area can stay square. */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
        {series.map((s) => (
          <Box
            key={s.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: COLOR[s.id] ?? 'rgba(150,155,165,0.45)',
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {s.id} ({s.data.length})
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
      >
        Products near each other taste alike. The axes have no units and no
        direction, and the distance between far-apart groups is not meaningful —
        read the neighbourhoods, not the layout.
        {offscreen > 0 &&
          ` ${offscreen} product${offscreen === 1 ? '' : 's'} fall outside this view.`}
      </Typography>
    </Box>
  );
};

export default FlavorSpaceMap;
